import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/Button';
import { FileText, RefreshCw, Download, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import {
  generateProFormaInvoiceHTML,
  htmlToPdfBase64,
  buildEpcQrDataUrl,
  type SupplierProfile,
} from '../../lib/pdfGenerator';
import type { Invoice } from '../../types/invoice';

const SUPPLIER_FIELDS = 'company_name, legal_entity_name, company_street, company_house_number, company_postal_code, company_city, company_country, company_address, company_phone, company_email, company_website, kvk_number, vat_number, company_tax_id, iban, bic, bank_name, logo_url, payment_terms_days';

interface ProductInfo {
  id: string;
  name: string;
  sku?: string;
  color_id?: string;
  size_id?: string;
}

function downloadPdfBase64(base64: string, filename: string) {
  const byteChars = atob(base64);
  const byteArray = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
  const blob = new Blob([byteArray], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const statusColors: Record<string, string> = {
  paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  unpaid: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  overdue: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
};

export const B2BInvoiceHistory: React.FC = () => {
  const { buyer } = useAuth();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Live lookup maps so qty/name/color/size are resolved from the products table
  // at render time (mirrors B2BReservationHistory). The denormalised invoice_items
  // columns are used only as a final fallback if a product was deleted.
  const [products, setProducts] = useState<Record<string, ProductInfo>>({});
  const [colors, setColors] = useState<Record<string, string>>({});
  const [sizes, setSizes] = useState<Record<string, string>>({});
  const supplierRef = useRef<SupplierProfile | null>(null);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: buyer?.preferred_currency || 'EUR',
    }).format(n);

  const resolveItem = useCallback((item: any) => {
    const p = products[item.product_id];
    const name =
      (p?.name && p.name.trim()) ||
      (item.product_name && String(item.product_name).trim()) ||
      (item.description ? String(item.description).split(' \u2014 ')[0] : '') ||
      'Item';
    const sku = p?.sku || item.sku || '';
    const colorName = p?.color_id ? colors[p.color_id] || '' : (item.color_name || '');
    const sizeName = p?.size_id ? sizes[p.size_id] || '' : (item.size_name || '');
    return { name, sku, colorName, sizeName };
  }, [products, colors, sizes]);

  const loadInvoices = useCallback(async () => {
    if (!buyer) return;
    setLoading(true);
    setError(null);
    try {
      // Two-query fetch: avoids PostgREST embedded-join schema-cache pitfalls
      // where invoice_items can come back as [] even when rows exist in DB.
      const { data: invs, error: invErr } = await supabase
        .from('invoices')
        .select('*')
        .eq('buyer_id', buyer.id)
        .order('invoice_date', { ascending: false });

      if (invErr) throw invErr;

      const invoiceIds = (invs || []).map(i => i.id);
      const itemsByInvoice: Record<string, any[]> = {};

      if (invoiceIds.length > 0) {
        // Try selecting the rich columns first; if the migration hasn't run yet,
        // fall back to the legacy column set so the UI still works.
        let items: any[] | null = null;
        const richSel = await supabase
          .from('invoice_items')
          .select('id, invoice_id, description, product_name, sku, color_name, size_name, quantity, unit_price, total_price, vat_rate, vat_amount, product_id')
          .in('invoice_id', invoiceIds);

        if (richSel.error) {
          if (richSel.error.code === '42703' || richSel.error.code === 'PGRST204') {
            const legacySel = await supabase
              .from('invoice_items')
              .select('id, invoice_id, description, quantity, unit_price, total_price, vat_rate, vat_amount, product_id')
              .in('invoice_id', invoiceIds);
            if (legacySel.error) throw legacySel.error;
            items = legacySel.data;
          } else {
            throw richSel.error;
          }
        } else {
          items = richSel.data;
        }

        for (const it of items || []) {
          (itemsByInvoice[it.invoice_id] ||= []).push(it);
        }
      }

      const merged = (invs || []).map(inv => ({
        ...inv,
        invoice_items: itemsByInvoice[inv.id] || [],
      }));
      setInvoices(merged);
    } catch (err: any) {
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [buyer]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // After invoices load, fetch the live products + product_colors + product_sizes
  // so render time can resolve item.product_id -> name/sku/color/size. This is the
  // exact pattern used by B2BReservationHistory which is known-good.
  useEffect(() => {
    const fetchProductsAndDicts = async () => {
      if (!buyer) return;
      const productIds = new Set<string>();
      invoices.forEach(inv => {
        (inv.invoice_items || []).forEach((item: any) => {
          if (item.product_id) productIds.add(item.product_id);
        });
      });

      const [productsRes, colorsRes, sizesRes] = await Promise.all([
        productIds.size > 0
          ? supabase.from('products').select('id, name, sku, color_id, size_id').in('id', Array.from(productIds))
          : Promise.resolve({ data: [], error: null } as any),
        supabase.from('product_colors').select('id, name').eq('admin_id', buyer.admin_id),
        supabase.from('product_sizes').select('id, name').eq('admin_id', buyer.admin_id),
      ]);

      if (productsRes.data) {
        const map: Record<string, ProductInfo> = {};
        productsRes.data.forEach((p: any) => { map[p.id] = p; });
        setProducts(map);
      }
      if (colorsRes.data) {
        const map: Record<string, string> = {};
        colorsRes.data.forEach((c: any) => { map[c.id] = c.name; });
        setColors(map);
      }
      if (sizesRes.data) {
        const map: Record<string, string> = {};
        sizesRes.data.forEach((s: any) => { map[s.id] = s.name; });
        setSizes(map);
      }
    };
    fetchProductsAndDicts();
  }, [invoices, buyer]);

  const getSupplier = async (): Promise<SupplierProfile | null> => {
    if (supplierRef.current) return supplierRef.current;
    if (!buyer) return null;
    const { data } = await supabase
      .from('users')
      .select(SUPPLIER_FIELDS)
      .eq('id', buyer.admin_id)
      .single();
    if (!data) return null;
    supplierRef.current = data as SupplierProfile;
    return supplierRef.current;
  };

  const handleDownload = async (raw: any) => {
    if (!buyer) return;
    setDownloadingId(raw.id);
    try {
      const supplier = await getSupplier();
      if (!supplier) throw new Error('Supplier profile unavailable');

      // Resolve each line from the live products table so the PDF reflects
      // the current name/sku/color/size, not the (possibly stale or empty)
      // snapshot stored in invoice_items.
      const resolvedItems = (raw.invoice_items || []).map((it: any) => {
        const r = resolveItem(it);
        return {
          ...it,
          product_name: r.name,
          sku: r.sku,
          color_name: r.colorName,
          size_name: r.sizeName,
          unit_price: parseFloat(it.unit_price),
          total_price: parseFloat(it.total_price),
          vat_rate: it.vat_rate != null ? parseFloat(it.vat_rate) : 0,
          vat_amount: it.vat_amount != null ? parseFloat(it.vat_amount) : 0,
        };
      });

      const inv: Invoice = {
        ...raw,
        user_id: raw.admin_id,
        subtotal_amount: parseFloat(raw.subtotal_amount),
        tax_amount: parseFloat(raw.tax_amount),
        total_amount: parseFloat(raw.total_amount),
        invoice_items: resolvedItems,
      };

      const epcQr = await buildEpcQrDataUrl(supplier, inv.total_amount, inv.invoice_number);
      const html = generateProFormaInvoiceHTML(inv, buyer, supplier, epcQr);
      // Full quality for explicit user download.
      const pdfBase64 = await htmlToPdfBase64(html);
      downloadPdfBase64(pdfBase64, `Invoice-${inv.invoice_number}.pdf`);
    } catch (err) {
      console.error('Failed to download invoice PDF:', err);
      alert('Failed to generate invoice PDF.');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Invoice History</h3>
        <Button variant="secondary" size="sm" onClick={loadInvoices}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {error && (
        <div className="card p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="card p-12 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">No invoices yet.</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Invoices are generated automatically when you place a reservation.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => {
            const isExpanded = expandedId === inv.id;
            const items = inv.invoice_items || [];
            return (
              <div key={inv.id} className="card overflow-hidden">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : inv.id)}
                  className="w-full px-4 py-3.5 flex items-start sm:items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors gap-3"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 min-w-0">
                    <span className="font-mono text-sm font-bold text-gray-800 dark:text-gray-200">
                      {inv.invoice_number}
                    </span>
                    <span
                      className={`self-start sm:self-auto px-2.5 py-0.5 text-xs font-semibold rounded-full capitalize ${
                        statusColors[inv.payment_status] || ''
                      }`}
                    >
                      {inv.payment_status}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 sm:hidden">
                      <Calendar className="h-3 w-3" /> {formatDate(inv.invoice_date)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="hidden sm:flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                      <Calendar className="h-3.5 w-3.5" /> {formatDate(inv.invoice_date)}
                    </span>
                    <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(parseFloat(inv.total_amount))}
                    </span>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700">
                    {inv.due_date && (
                      <div className="flex items-center gap-2 mt-3 mb-3 text-sm text-gray-600 dark:text-gray-400">
                        <Calendar className="h-4 w-4 flex-shrink-0" />
                        <span>
                          Due:{' '}
                          <span className="font-semibold text-gray-800 dark:text-gray-200">
                            {formatDate(inv.due_date)}
                          </span>
                        </span>
                      </div>
                    )}

                    {/* Desktop table */}
                    <div className="hidden sm:block overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            <th className="pb-2 text-center font-semibold w-16">Qty</th>
                            <th className="pb-2 font-semibold">Description</th>
                            <th className="pb-2 text-right font-semibold w-28">Unit Price</th>
                            <th className="pb-2 text-right font-semibold w-28">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {items.map((item: any) => {
                            const r = resolveItem(item);
                            const variant = [r.colorName, r.sizeName].filter(Boolean).join(' / ');
                            return (
                              <tr key={item.id}>
                                <td className="py-2.5 text-center font-semibold">{item.quantity}</td>
                                <td className="py-2.5 text-gray-800 dark:text-gray-200">
                                  <div className="font-medium">
                                    {r.name}
                                    {r.sku && (
                                      <span className="text-gray-400 dark:text-gray-500 font-normal ml-1">
                                        ({r.sku})
                                      </span>
                                    )}
                                  </div>
                                  {variant && (
                                    <div className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">
                                      {variant}
                                    </div>
                                  )}
                                </td>
                                <td className="py-2.5 text-right text-gray-600 dark:text-gray-400">
                                  {formatCurrency(parseFloat(item.unit_price))}
                                </td>
                                <td className="py-2.5 text-right font-bold text-gray-900 dark:text-gray-100">
                                  {formatCurrency(parseFloat(item.total_price))}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile card list */}
                    <div className="sm:hidden space-y-2 mt-1">
                      {items.map((item: any) => {
                        const r = resolveItem(item);
                        const variant = [r.colorName, r.sizeName].filter(Boolean).join(' / ');
                        return (
                          <div
                            key={item.id}
                            className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 flex items-start justify-between gap-3"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug break-words">
                                {r.name}
                              </p>
                              {r.sku && (
                                <p className="text-xs font-mono text-gray-400 dark:text-gray-500">
                                  {r.sku}
                                </p>
                              )}
                              {variant && (
                                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">
                                  {variant}
                                </p>
                              )}
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {formatCurrency(parseFloat(item.unit_price))} each
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                ×{item.quantity}
                              </p>
                              <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mt-0.5">
                                {formatCurrency(parseFloat(item.total_price))}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Totals + actions */}
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                        <span>Subtotal</span>
                        <span className="font-medium">
                          {formatCurrency(parseFloat(inv.subtotal_amount))}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                        <span>VAT</span>
                        <span className="font-medium">
                          {formatCurrency(parseFloat(inv.tax_amount))}
                        </span>
                      </div>
                      <div className="flex justify-between text-base font-bold text-gray-900 dark:text-gray-100 pt-1">
                        <span>Total</span>
                        <span className="text-blue-600 dark:text-blue-400">
                          {formatCurrency(parseFloat(inv.total_amount))}
                        </span>
                      </div>
                      <div className="pt-2 flex justify-end">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleDownload(inv)}
                          disabled={downloadingId === inv.id}
                        >
                          <Download className="h-4 w-4 mr-1.5" />
                          {downloadingId === inv.id ? 'Generating...' : 'Download PDF'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
